import axios from 'axios';
import { CmsPageResponse, ContactUsResponse, InitSiteData } from 'ts/shared/interfaces/CmsPageResponse';
import { ContactUsModel } from 'ts/shared/classes/ContactUsModel';
import { getScript } from 'utils/utils';
import { TermsResponse } from 'ts/shared/interfaces/Generic';
import {
  CreateProposalParams,
  CreateProposalResponse,
  UpdateProposalParams,
} from 'ts/shared/interfaces/CreateProposalParams';

export const getSpecialTerms = async (params: any): Promise<any[] | undefined> => {
  try {
    const url = 'IlPackages/Terms';
    return (await axios.post(url, params)).data?.listMessages;
  } catch (err) {
    return undefined;
  }
};

let firstLoad = true;
export const getPage = async (
  location?: string,
  b2b?: boolean,
): Promise<CmsPageResponse | undefined> => {
  try {
    const lang = localStorage.getItem('language') || 'he';
    let data;
    let url = 'data';

    if (location && location[0] !== '?') {
      url = `data/${location}`;
    }
    const refreshKey = sessionStorage.getItem('refreshKey');
    const params = {
      language: lang === 'ru' ? 'ru' : 'he',
      loadStaticSections: !sessionStorage.getItem('siteData'),
      refreshKey,
    };

    if (url === 'data') {
      data = (await axios.post<CmsPageResponse>(url, params)).data;
    } else {
      data = (await axios.get<CmsPageResponse>(url, { params })).data;
    }
    sessionStorage.setItem('refreshKey', data.refreshKey);

    if (data.needRefresh) {
      sessionStorage.removeItem('siteData');
      if (!b2b) {
        localStorage.removeItem('allLoadings');
        getLoadings();
      }
    }

    const siteData = sessionStorage.getItem('siteData');

    if (siteData) {
      data.pageHeader = JSON.parse(siteData).pageHeader;
      data.pageFooter = JSON.parse(siteData).pageFooter;
      data.siteConfig = JSON.parse(siteData).siteConfig;
      data.clubLogoUrl = JSON.parse(siteData).clubLogoUrl;
      data.clubDiscountText = JSON.parse(siteData).clubDiscountText;
    } else if (data.pageHeader && data.pageFooter && data.siteConfig) {
      sessionStorage.setItem(
        'siteData',
        JSON.stringify({
          pageHeader: data.pageHeader,
          pageFooter: data.pageFooter,
          siteConfig: data.siteConfig,
          clubLogoUrl: data.clubLogoUrl,
          clubDiscountText: data.clubDiscountText,
        }),
      );
    }

    if (data.page) {
      if (data.page.languages?.length) {
        const details = lang === 'he' ? data.page.languages[0] : data.page.languages[1];

        if (firstLoad) {
          firstLoad = false;
          const cookiesAccepted = localStorage.getItem('cookie-policy-result');

          sessionStorage.setItem(
            'cms_scripts',
            JSON.stringify({
              anotherAnalytics: data.siteConfig.anotherAnalytics,
              googleAnalytics: data.siteConfig.googleAnalytics,
              googleTagManager: data.siteConfig.googleTagManager,
              script1: data.siteConfig.script1,
              script2: data.siteConfig.script2,
              script3: data.siteConfig.script3,
            }),
          );
          if (cookiesAccepted) {
            loadAnalytics();
          }
        }

        // if (lang !== 'ru') {
        //   const seoTitle = details.seo.title || data.page.languages[0].seo.title || '';
        //   const siteName = i18n.t('site_name');
        //   document.title = seoTitle ? seoTitle + ' | ' + siteName : siteName;
        // }

        const canonicalLink = details.seo.canonical || data.page.languages[0].seo.canonical;
        if (canonicalLink) {
          const canonical = document.createElement('link');
          canonical.rel = 'canonical';
          canonical.href = canonicalLink;
          document.head.appendChild(canonical);
        }

        const keywordsDesc = details.seo.keywords || data.page.languages[0].seo.keywords;
        if (keywordsDesc) {
          const keywordsMeta = document.createElement('meta');
          keywordsMeta.name = 'keywords';
          keywordsMeta.content = keywordsDesc;
          document.head.appendChild(keywordsMeta);
        }

        const descDesc = details.seo.description || data.page.languages[0].seo.description;
        if (descDesc) {
          const descMeta = document.createElement('meta');
          descMeta.name = 'description';
          descMeta.content = descDesc;
          document.head.appendChild(descMeta);
        }
      }
    }

    return data;
  } catch (err) {
    const siteData = sessionStorage.getItem('siteData');
    if (siteData) {
      const siteDataObj = JSON.parse(siteData);
      const data = {
        pageHeader: siteDataObj.pageHeader,
        pageFooter: siteDataObj.pageFooter,
        siteConfig: siteDataObj.siteConfig,
        clubLogoUrl: siteDataObj.clubLogoUrl,
        clubDiscountText: siteDataObj.clubDiscountText,
      };
      return data as unknown as CmsPageResponse;
    }
    return undefined;
  }
};

export const getLoadings = async (): Promise<CmsPageResponse | undefined> => {
  const loadings = localStorage.getItem('allLoadings');
  const lang = localStorage.getItem('language') || 'he';

  const oneHourAgo = Date.now() - 1000 * 60 * 60;
  if (loadings) {
    const loadingsObj = JSON.parse(loadings);
    if (loadingsObj.timestamp && loadingsObj.timestamp < oneHourAgo) {
      localStorage.removeItem('allLoadings');
    } else {
      return;
    }
  }
  try {
    const url = 'data/AllLoadings';

    const { data } = await axios.get(url, { params: { language: lang === 'ru' ? 'ru' : 'he' } });

    localStorage.setItem('allLoadings', JSON.stringify({ ...data, timestamp: Date.now() }));
  } catch (err) {
    return undefined;
  }
};

export const sendContactUs = async (
  data: ContactUsModel,
): Promise<ContactUsResponse | undefined> => {
  try {
    const url = 'Utils/ContactUs';
    return (await axios.post(url, data)).data;
  } catch (err) {
    return undefined;
  }
};

export const sendCancelOrder = async (
  data: ContactUsModel,
): Promise<ContactUsResponse | undefined> => {
  try {
    const url = 'Utils/CancelOrder';
    return (await axios.post(url, data)).data;
  } catch (err) {
    return undefined;
  }
};

export const getTerms = async (
  product?: string,
  name?: string,
): Promise<TermsResponse | undefined> => {
  try {
    const language = localStorage.getItem('language') || 'he';
    const url = 'data/Terms';
    return (await axios.get(url, { params: { language, product, name } })).data.terms;
  } catch (err) {
    return undefined;
  }
};

export const initSite = async (): Promise<InitSiteData | undefined> => {
  const dataStored = localStorage.getItem('initSiteData');
  if (dataStored) {
    const data = JSON.parse(dataStored);
    const oneHourAgo = Date.now() - 1000 * 60 * 60;
    if (data.timestamp && data.timestamp < oneHourAgo) {
      localStorage.removeItem('initSiteData');
    } else {
      return data;
    }
  }

  try {
    const response = await axios.get('data/init');
    localStorage.setItem(
      'initSiteData',
      JSON.stringify({ ...response.data, timestamp: Date.now() }),
    );

    return response.data;
  } catch (error) {
    return undefined;
  }
};

export const createProposal = async (
  params: CreateProposalParams,
): Promise<CreateProposalResponse | undefined> => {
  try {
    const url = 'Utils/CreateProposalDynamic';

    return (await axios.post(url, params)).data;
  } catch (err) {
    return undefined;
  }
};

export const updateProposalD = async (
  params: UpdateProposalParams,
): Promise<CreateProposalResponse | undefined> => {
  try {
    const url = 'Utils/UpdateProposalDynamic';

    return (
      await axios.post(url, { ...params, previewKeyPublic: params.previewKeyPublic?.toUpperCase() })
    ).data;
  } catch (err) {
    return undefined;
  }
};
export const deleteProposalD = async (id: number): Promise<any | undefined> => {
  try {
    const url = 'Utils/DeleteProposalDynamic';

    return (await axios.post(url, { proposalId: id })).data;
  } catch (err) {
    return undefined;
  }
};

export const sendEmailProposal = async (
  email: string,
  url: string,
  previewKeyPublic: string,
  hotelNames: string[],
  publicProposalUrl?: string,
): Promise<any> => {
  try {
    return (
      await axios.post('/Hotels/SendProposalPdfByEmail', {
        email,
        url,
        previewKeyPublic: previewKeyPublic?.toUpperCase(),
        publicProposalUrl,
        hotelNames: hotelNames,
      })
    ).data;
  } catch (err) {
    return undefined;
  }
};

export const sendEmailProposalPublic = async (
  email: string,
  url: string,
  previewKeyPublic: string,
  hotelNames: string[],
  publicProposalUrl?: string,
): Promise<any> => {
  try {
    return (
      await axios.post('/Hotels/SendProposalPdfByEmailPublic', {
        email,
        url,
        previewKeyPublic: previewKeyPublic?.toUpperCase(),
        publicProposalUrl,
        hotelNames: hotelNames,
      })
    ).data;
  } catch (err) {
    return undefined;
  }
};

export const getHFee = async (name: string): Promise<any | undefined> => {
  try {
    const params = { language: 'he', product: name };
    const url = 'data/HandlingFees';
    return (await axios.get(url, { params })).data?.fees.title;
  } catch (err) {
    return undefined;
  }
};
export const getSearchFilter = async (): Promise<any | undefined> => {
  const key = 'hotelsSearchFilters';
  const cached = sessionStorage.getItem(key);
  if (cached) {
    return JSON.parse(cached);
  } else {
    try {
      const url = 'Hotels/GetSearchFilters';
      const { data } = await axios.get(url);
      sessionStorage.setItem(key, JSON.stringify(data));
      return data;
    } catch (err) {
      return undefined;
    }
  }
};

export const formatUrl = (url?: string) => {
  if (url?.includes('https://www.travellertours.co.il')) {
    return url.replace('https://www.travellertours.co.il', '');
  }

  if (url?.includes('https://www.netravel.co.il')) {
    return url.replace('https://www.netravel.co.il', '');
  }

  if (url?.includes('http://www.netravel.co.il')) {
    return url.replace('http://www.netravel.co.il', '');
  }

  return url || '';
};

export const loadAnalytics = () => {
  const scriptsTxt = sessionStorage.getItem('cms_scripts');
  const scripts = JSON.parse(scriptsTxt || '{}');

  if (scripts.anotherAnalytics) {
    const comment = document.createComment(' Another Analytics ');
    document.head.appendChild(comment);
    const script = getScript(scripts.anotherAnalytics);
    document.head.appendChild(script);
  }

  if (scripts.googleAnalytics) {
    const script = getScript(scripts.googleAnalytics, 'Google Analytics');
    document.body.appendChild(script);
  }

  if (scripts.googleTagManager) {
    const script = getScript(scripts.googleTagManager, 'Google Tag Manager');
    document.head.appendChild(script);
  }

  if (scripts.script1) {
    const script = getScript(scripts.script1, 'Script 1');
    document.body.appendChild(script);
  }

  if (scripts.script2) {
    const script = getScript(scripts.script2, 'Script 2');
    document.body.appendChild(script);
  }

  if (scripts.script3) {
    const script = getScript(scripts.script3, 'Script 3');
    document.body.appendChild(script);
  }
};
